import * as React from "react";
import Downshift, {
  ChildrenFunction,
  ControllerStateAndHelpers,
  DownshiftProps
} from "downshift";

import { ISelectItem } from "../Forms";

type IMultiDownshift = DownshiftProps<ISelectItem> & {
  children: ChildrenFunction<ISelectItem>;
  handleChange?: (
    action: "add" | "remove",
    affectedItem: ISelectItem | null,
    oldSelectedItems: ISelectItem[],
    stateAndHelpers?: ControllerStateAndHelpers<ISelectItem>
  ) => void;
  onSelect?: (
    action: string,
    affectedItem: ISelectItem | null,
    oldSelectedItems: ISelectItem[],
    stateAndHelpers?: ControllerStateAndHelpers<ISelectItem>
  ) => void;
  defaultValues?: ISelectItem[];
  disabledOptions?: string[];
  singleSelection?: boolean;
  disableCallBack?: (disable: boolean) => void;
};

/**
 * Very basic component handling multiselecting making use of Downshift.
 */
const MultiDownshift: React.FC<IMultiDownshift> = ({
  children,
  onSelect,
  handleChange,
  itemToString,
  defaultValues = [],
  disabledOptions,
  disableCallBack,
  singleSelection

}) => {
  const [ selectedItems, setSelectedItems ] = React.useState<ISelectItem[]>([]);

  React.useEffect(() => {
    if (defaultValues.length) {
      setSelectedItems(defaultValues);
    }
  }, [ defaultValues ]);

  React.useEffect(() => {
    if (singleSelection && disableCallBack && selectedItems.length > 0) {
      disableCallBack(true);
    } else if (disableCallBack) {
      disableCallBack(false);
    }
  }, [
    disableCallBack,
    selectedItems.length,
    singleSelection
  ]);

  /**
   * Handles selection. Remove / add an item from the list of selected
   * items and call onSelect/handleChange callbacks.
   */
  const handleSelection = (selectedItem: any, downshift: any) => {
    const callOnChange = (action: "add" | "remove") => {
      return () => {
        if (onSelect) {
          onSelect(action, selectedItem, selectedItems, getStateAndHelpers(downshift));
        }

        if (handleChange) {
          handleChange(action, selectedItem, selectedItems, getStateAndHelpers(downshift));
        }
      };
    };

    if (selectedItems.includes(selectedItem)) {
      removeItem(selectedItem, callOnChange("remove"));
    } else {
      // dont allow select of disabled options
      if (disabledOptions?.includes(selectedItem.value)) {
        return;
      }

      addSelectedItem(selectedItem, callOnChange("add"));
    }
  };

  /**
   * Remove an item from the list of selected.
   *
   * @param item
   * @param cb
   */
  const removeItem = (item: ISelectItem, cb?: any): void => {
    setSelectedItems(selectedItems.filter(i => i !== item));

    if (cb) {
      cb();
    }
  };

  /**
   * Add an item to the list of selected.
   *
   * @param item
   * @param cb
   */
  const addSelectedItem = (item: ISelectItem, cb: any): void => {
    setSelectedItems([ ...selectedItems, item ]);

    if (cb) {
      cb();
    }
  };

  /**
   * Get props for buttons that allow the removal of selected items.
   */
  const getRemoveButtonProps = ({
    onClick, item, ...props
  }: { [p: string]: any } = {}): { onClick: (e: any) => void } => {
    return {
      onClick: (e: any) => {
        const callOnChange = (action: "add" | "remove", item: any, selectedItems: any) => {
          return () => {
            if (handleChange) {
              handleChange(action, item, selectedItems);
            }
          };
        };

        onClick && onClick(e);
        e.stopPropagation();
        removeItem(item, callOnChange("remove", item, selectedItems));
      },
      ...props
    };
  };

  /**
   * Get component state and helpers (normally, to present them to children).
   */
  const getStateAndHelpers = (
    downshift: ControllerStateAndHelpers<ISelectItem>
  ): ControllerStateAndHelpers<ISelectItem> & {
    getRemoveButtonProps: ({
      onClick, item, ...props
    }: { [p: string]: any }) => { onClick: (e: any) => void };
    removeItem: (item: ISelectItem, cb?: any) => void;
    selectedItems: ISelectItem[];
  } => {
    return {
      getRemoveButtonProps,
      removeItem,
      selectedItems,
      ...downshift
    };
  };

  return (
    <>
      <Downshift
        onChange={handleSelection}
        itemToString={itemToString}
      >
        {downshift => children(getStateAndHelpers(downshift))}
      </Downshift>
    </>
  );
};

export default MultiDownshift;
